// Central registry of feature prerequisites.
// Each entry: how to detect the unmet state, what to call it, and where to send the user to fix it.
//
// To add a new prerequisite:
//   1. Add an entry below keyed by a short identifier.
//   2. Wrap the target page in <PrerequisiteGate requires={['yourKey']} ... />.

// Countries whose numbers Twilio requires a regulatory address bundle for.
// Keep in sync with server-side enforcement in user.controller.js.
export const KYC_REQUIRED_COUNTRIES = ['GB', 'FR', 'DE', 'NL', 'HK', 'PR'];

export const PREREQUISITES = {
  hasNumber: {
    title: 'Get a phone number',
    description: 'You need at least one NumFree number first.',
    note: 'US and Canadian numbers are available right away. Numbers in some countries (UK, FR, DE, NL, HK, PR) also need a verified address for KYC — you’ll be prompted on the purchase screen if it applies.',
    check: (user) => Array.isArray(user?.freenumbers) && user.freenumbers.length > 0,
    fixPath: '/numbers',
    fixLabel: 'Get a number',
  },

  // Verified regulatory address (KYC) — required by some carrier countries before
  // a number in that country can be issued. The check is async because the data
  // lives on /api/users/numfree/regulatory, not on the JWT user object.
  // Wire on a page only when that page genuinely cannot proceed without it; for
  // generic browse/buy flows, surface it inline on the page instead of blocking.
  hasVerifiedAddress: {
    title: 'Verify your address (KYC)',
    description: 'Some countries require a verified address on file before issuing a number, for regulatory compliance.',
    note: `Required for numbers in: ${KYC_REQUIRED_COUNTRIES.join(', ')}. Not required for US/CA.`,
    asyncCheck: async (authFetch) => {
      try {
        const r = await authFetch('/api/users/numfree/regulatory');
        if (!r.ok) return false;
        const list = await r.json();
        return Array.isArray(list) && list.some(v => (v.status || '').toLowerCase().indexOf('approved') !== -1);
      } catch {
        return false;
      }
    },
    fixPath: '/verification',
    fixLabel: 'Verify address',
  },
};

// Resolve a list of requirement keys against the current user.
// Sync requirements are evaluated immediately; async requirements are caller-resolved.
// Returns: { met, unmet: [{ key, ...config }], asyncKeys: [...] }
export function evaluatePrerequisitesPartial(requirements, user, asyncResults = {}) {
  const unmet = [];
  const asyncKeys = [];
  for (const key of requirements || []) {
    const def = PREREQUISITES[key];
    if (!def) continue;
    if (def.asyncCheck) {
      asyncKeys.push(key);
      if (asyncResults[key] === false) {
        unmet.push({ key, ...def });
      }
    } else if (!def.check(user)) {
      unmet.push({ key, ...def });
    }
  }
  return { met: unmet.length === 0, unmet, asyncKeys };
}

// Sync-only evaluation (kept for backward compatibility — async checks are skipped).
export function evaluatePrerequisites(requirements, user) {
  const unmet = [];
  for (const key of requirements || []) {
    const def = PREREQUISITES[key];
    if (!def || def.asyncCheck) continue;
    if (!def.check(user)) {
      unmet.push({ key, ...def });
    }
  }
  return { met: unmet.length === 0, unmet };
}
